import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { BrandColor } from '@styleseat/brand';
import { SnackbarContext } from '../../shared/SnackbarContext';
import analytics from '../../../modules/analytics';
import ssPropTypes from '../../../modules/ssPropTypes';
import { createRatingResponse } from '../../../modules/provider/reviews';

import {
  Button,
  ConfirmationDialog,
  FormInput,
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayModal,
  HeaderText,
} from '../../ui';
import './ReviewResponseModal.scss';

class ReviewResponseModal extends React.Component {
  static propTypes = {
    // Review the pro is responding to
    review: PropTypes.object.isRequired,
    // True if editing existing response
    edit: PropTypes.bool,
    // ID of the logged in provider
    proId: ssPropTypes.numberLike.isRequired,
    // Callback function to update list of reviews
    refreshReviews: PropTypes.func,
    onClose: PropTypes.func,
  };

  static contextType = SnackbarContext;

  static defaultProps = {
    edit: true,
    refreshReviews: undefined,
    onClose: () => {},
  };

  state = {
    responseText: '',
    shrinkClientReview: undefined,
    showConfirmationDialog: false,
  };

  containerRef = React.createRef();

  showExpandTextButton = null;

  componentDidMount() {
    const { response } = this.props.review;

    if (response) {
      this.setState({ responseText: response.text });
    }
    this.showMoreButton();
  }

  componentDidUpdate() {
    this.showMoreButton();
  }

  /**
   * Check if container needs show more button
   */
  showMoreButton() {
    const clientReview = this.containerRef.current;
    if (!clientReview || this.showExpandTextButton !== null) {
      return;
    }
    const scrollableDistance = clientReview.scrollHeight - clientReview.offsetHeight;
    // If text block exceeds height of 2 lines,
    // hide the rest and surface show more button
    if (clientReview && scrollableDistance >= 2) {
      this.showExpandTextButton = true;
      this.setState({ shrinkClientReview: true });
    } else {
      this.showExpandTextButton = false;
    }
  }

  /**
   * Save pro response to client review
   */
  handleSaveResponse = () => {
    const { review } = this.props;

    // Save response to review
    createRatingResponse(
      this.props.proId,
      this.props.review.id,
      this.state.responseText,
    ).then(() => {
      const updatedOrPosted = this.props.edit ? 'updated' : 'posted';
      // Reload list of reviews
      if (typeof this.props.refreshReviews === 'function') {
        this.props.refreshReviews(review);
      }
      analytics.track('P Clicked Reply Review Confirmed');
      const { open: openSnackbar } = this.context;
      openSnackbar({
        icon: 'success-icon',
        sideColor: BrandColor.Green,
        inlineHighlight: `Your reply has been ${updatedOrPosted}.`,
        timeOut: 5000,
      });
      this.props.onClose();
    }).catch(() => {
      const { open: openSnackbar } = this.context;
      openSnackbar({
        icon: 'error-icon',
        inlineHighlight: 'Something went wrong. Please try again in a minute.',
        timeOut: 5000,
      });
    }).finally(() => {
      this.setState({ showConfirmationDialog: false });
    });
  };

  /**
   * Updates response to client review
   *
   * @param {Object} event - response text object
   */
  handleResponseTextChange = event => {
    this.setState({ responseText: event.target.value });
  };

  /**
   * Expand or collapse client review text box
   */
  handleToggleTextBox = () => {
    this.setState(prevState => ({ shrinkClientReview: !prevState.shrinkClientReview }));
  };

  render() {
    const {
      review, edit, onClose,
    } = this.props;
    const {
      shrinkClientReview, responseText, showConfirmationDialog,
    } = this.state;
    const clientFirstName = review.appointment.client.user.first_name;
    const clientLastName = review.appointment.client.user.last_name[0];
    const responseLimit = 800;
    const charactersRemaining = responseLimit - responseText.length;
    const characterString = charactersRemaining === 1 ? 'character' : 'characters';
    const responseCounter = responseText.length > 0
      ? `${charactersRemaining} ${characterString} remaining`
      : `Max ${responseLimit} characters`;

    return (
      <React.Fragment>
        <PortalOverlay name="ReviewResponseModal">
          <PortalOverlayModal
            position="fullScreen"
          >
            <PortalOverlayHeader
              renderTitle={() => <HeaderText level={4}>Respond to {clientFirstName}...</HeaderText>}
              renderLeftAction={() => (
                <Button
                  onClick={onClose}
                  testID="review-response__modal-cancel"
                  appearance="text-primary"
                  style={{
                    color: BrandColor.Metal,
                  }}
                >
                  Cancel
                </Button>
              )}
              renderRightAction={() => (
                <Button
                  onClick={() => this.setState({ showConfirmationDialog: true })}
                  disabled={!responseText}
                  testID="review-response__modal-save"
                  appearance="text-primary"
                >
                  Post
                </Button>
              )}
            />
            <div className="view-response">
              <section className="review-container" data-testid="review-response__container">
                <p
                  className={classNames(
                    'review-text',
                    {
                      'expand-text-container': shrinkClientReview === false,
                    },
                  )}
                  ref={this.containerRef}
                >
                  &ldquo;{ review.review_text }&rdquo;
                </p>
                <div className="review-details">
                  <span className="review-info">
                    {clientFirstName} {clientLastName}. &#8226; { review.date }
                  </span>
                  {this.showExpandTextButton && (
                    <button className="expand-text-button" onClick={this.handleToggleTextBox} type="button">
                      { shrinkClientReview ? 'Read more' : 'Read less' }
                    </button>
                  )}
                </div>
              </section>
              <div className="text-count-container" data-testid="review-response__text-count">
                {responseCounter}
              </div>
              <FormInput
                type="textarea"
                value={responseText}
                onChange={this.handleResponseTextChange}
                placeholder="Respond here..."
                maxLength="800"
                testID="review-response__textarea"
              />
            </div>
          </PortalOverlayModal>
        </PortalOverlay>
        <ConfirmationDialog
          show={showConfirmationDialog}
          title={`This ${edit ? 'edit' : 'post'} is public`}
          description="Your response will be visible on your page and your client will be notified."
          confirmText="Post"
          onConfirm={this.handleSaveResponse}
          cancelText="Nevermind"
          onCancel={() => this.setState({ showConfirmationDialog: false })}
        />
      </React.Fragment>
    );
  }
}

export default ReviewResponseModal;
